import { takeLayer } from '@/lib/layers.js'
import { defineStore } from 'pinia'

let nextId = 1

/** Toasts, the confirmation dialog, the mobile menu and the stack of create panels. */
export const useUi = defineStore('ui', {
  state: () => ({
    toasts: [],
    confirm: null,
    // The fields a write needs and the record does not have (D-343): {entity, record, names, inputs, errors, title, message, resolve}.
    fields: null,
    menuOpen: false,
    // Create panels sliding in from the right, one over another (Dynamics' quick create):
    // [{id, entity, defaults, onSaved, from}]. The top one is the one being filled in.
    creates: [],
    savedRecord: null,
    recent: [],
  }),
  actions: {
    toast(message, { kind = 'info', timeout = 4500, action = null } = {}) {
      const id = nextId++
      this.toasts.push({ id, message, kind, action })
      if (timeout) setTimeout(() => this.dismiss(id), timeout)
      return id
    },
    success(message) {
      return this.toast(message, { kind: 'success' })
    },
    error(message) {
      return this.toast(message, { kind: 'error', timeout: 8000 })
    },
    warn(message) {
      return this.toast(message, { kind: 'warning', timeout: 7000 })
    },
    dismiss(id) {
      this.toasts = this.toasts.filter((t) => t.id !== id)
    },
    /** @returns {Promise<boolean>} */
    ask({ title, message, confirmLabel, danger = false }) {
      return new Promise((resolve) => {
        this.confirm = { title, message, confirmLabel, danger, resolve }
      })
    },
    answer(value) {
      this.confirm?.resolve(value)
      this.confirm = null
    },
    /** @returns {Promise<object|null>} the fields' values as a write's body adds them, or null when cancelled */
    askFields({ entity, record = null, names, inputs, errors = {}, title = '', message = '' }) {
      return new Promise((resolve) => {
        this.fields = { entity, record, names, inputs, errors, title, message, resolve }
      })
    },
    answerFields(patch) {
      this.fields?.resolve(patch)
      this.fields = null
    },
    /**
     * Open a panel to create a record, over whatever is on screen (a page, a form, another
     * panel), without leaving it. `onSaved(record, result)` runs after it was saved (or queued);
     * `from` names what it is being created for ("Nowa szansa").
     */
    openCreate({ entity, defaults = {}, onSaved = null, from = '' }) {
      const id = nextId++
      this.creates.push({ id, entity, defaults, onSaved, from, layer: takeLayer() })
      return id
    },
    closeCreate(id) {
      this.creates.filter((c) => c.id === id).forEach((c) => c.layer?.release())
      this.creates = this.creates.filter((c) => c.id !== id)
    },
    /** Open an existing record in a panel from the right, over whatever is on screen (on the same stack as the create panels). */
    openRecord({ entity, uuid }) {
      const top = this.creates[this.creates.length - 1]
      if (top?.kind === 'record' && top.entity === entity && top.uuid === uuid) return top.id
      const id = nextId++
      this.creates.push({ id, kind: 'record', entity, uuid, layer: takeLayer() })
      return id
    },
    /** A record changed in a panel: the page under it reads it again (its figures, its lists). */
    recordSaved(entity, uuid) {
      this.savedRecord = { entity, uuid, at: Date.now() }
    },
    closeAllPanels() {
      this.creates.forEach((c) => c.layer?.release())
      this.creates = []
    },
    remember(record) {
      if (!record?.uuid || !record.entity) return
      const item = { entity: record.entity, uuid: record.uuid, label: record.label, number: record.number }
      this.recent = [item, ...this.recent.filter((r) => r.uuid !== record.uuid)].slice(0, 12)
      try {
        localStorage.setItem('addcrm:recent', JSON.stringify(this.recent))
      } catch {
        // recent items are a convenience
      }
    },
    restoreRecent() {
      try {
        this.recent = JSON.parse(localStorage.getItem('addcrm:recent') || '[]')
      } catch {
        this.recent = []
      }
    },
  },
})
